import { Link } from 'react-router-dom';
import { useComparison } from '../services/ComparisonContext';
import { X, ArrowRight, Scale, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCountry } from '../context/CountryContext';
import { API_BASE_URL } from '../config';

const ComparisonFloatingBar = () => {
    const { selectedProperties, removeFromCompare, clearComparison } = useComparison();
    const { activeCountry } = useCountry();
    const getPath = (path: string) => {
        if (path.startsWith('http') || path.startsWith('/admin')) return path;
        const prefix = activeCountry ? `/${activeCountry.code.toLowerCase()}` : '';
        if (path === '/') return prefix || '/';
        return `${prefix}${path.startsWith('/') ? path : '/' + path}`;
    };

    // Helper for images
    const getImageUrl = (url?: string) => {
        if (!url) return null;
        return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    };

    if (selectedProperties.length === 0) return null;

    return (
        <AnimatePresence>
            <motion.div 
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 w-auto"
            >
                {/* Floating Bar */}
                <div 
                    className="flex items-center gap-3 md:gap-6 bg-white dark:bg-[#111] pl-5 pr-2 py-2 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-slate-200 dark:border-white/10"
                >
                    {/* Label Section */}
                    <div className="flex items-center gap-3 border-r border-slate-200 dark:border-white/10 pr-6">
                        <div className="relative">
                            <Scale className="w-5 h-5 text-slate-900 dark:text-white" />
                            <span className="absolute -top-2 -right-2 w-4 h-4 bg-brand-700 text-white text-[9px] font-bold flex items-center justify-center rounded-full border border-white dark:border-zinc-900">
                                {selectedProperties.length}
                            </span>
                        </div>
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-900 dark:text-white hidden sm:block">Compare List</span>
                    </div>

                    {/* Previews */}
                    <div className="flex -space-x-3">
                        {selectedProperties.slice(0, 3).map((p) => (
                            <div 
                                key={p.id}
                                className="relative w-10 h-10 rounded-full border-2 border-white dark:border-[#111] overflow-hidden bg-gray-100 dark:bg-zinc-800 group"
                            >
                                {p.image_url || ((p as any).images && (p as any).images[0]) ? (
                                    <img 
                                        src={getImageUrl(p.image_url || ((p as any).images && (p as any).images[0]?.image_url)) || ''} 
                                        className="w-full h-full object-cover" 
                                        alt={p.title}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[8px] text-gray-400">N/A</div>
                                )}
                                {/* Remove overlay */}
                                <button 
                                    onClick={() => removeFromCompare(p.id)}
                                    className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <X className="w-4 h-4 text-white" />
                                </button>
                            </div>
                        ))}
                        {[...Array(Math.max(0, 3 - selectedProperties.length))].map((_, i) => (
                            <div 
                                key={`placeholder-${i}`}
                                className="w-10 h-10 rounded-full border-2 border-white dark:border-[#111] bg-gray-50 dark:bg-white/5 flex items-center justify-center text-[10px] text-gray-300 dark:text-gray-600 font-bold"
                            >
                                N/A
                            </div>
                        ))}
                         {selectedProperties.length > 3 && (
                            <div className="w-10 h-10 rounded-full border-2 border-white dark:border-[#111] bg-gray-100 dark:bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400 z-10">
                                +{selectedProperties.length - 3}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pl-2 md:pl-4 border-l border-slate-200 dark:border-white/10">
                         <button 
                            onClick={clearComparison}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            title="Clear All"
                        >
                            <Trash2 size={18} />
                        </button>
                        
                        <Link 
                            to={getPath("/compare")} 
                            className="bg-accent-500 hover:bg-accent-600 text-white text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full shadow-sm flex items-center gap-2 transition-all hover:scale-105"
                        >
                            Compare <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ComparisonFloatingBar;
